/**
 * ImportWizard - Main component for Excel file import and API mapping
 * author: Generated Component
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, ProgressBar, Row } from 'react-bootstrap';
import { withTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import { RegulationContext } from '../../App';
import ImportService from '../../core/service/ImportService';
import Utils from '../../core/utilities/Utils';
import ReportingPeriod from '../Regulation/ReportingPeriod';
import ColumnMapper from './ColumnMapper';
import FileUpload from './FileUpload';

import SheetSelector from './SheetSelector';

// Export for testing
export const transformDataToApiFormat = (mappings, sheet, period) => {
  // Get the data rows from the selected sheet
  const dataRows = sheet.data || [];

  // Process datapoint mappings
  const dataPointMappings = mappings.dataPointMappings || [];

  // Process branch mappings
  const branchMappings = mappings.branchMappings || [];

  // Create the result array - one object per datapoint mapping
  const result = [];

  // Check if we should use all datapoints from a column
  if (mappings.useAllDatapointsFromColumn && mappings.dataPointCode) {
    // Get the datapoint column index
    const dataPointColumnIndex = sheet.columns.findIndex(col => col.name === mappings.dataPointCode);
    
    if (dataPointColumnIndex >= 0) {
      // Process each row in the sheet
      dataRows.forEach((row, rowIndex) => {
        const dataPointCode = row[dataPointColumnIndex];
        if (!dataPointCode) return; // Skip empty datapoint codes

        // Create branch data for this row
        const branchData = [];

        // For each branch mapping, create a branch data entry
        branchMappings.forEach(mapping => {
          if ((mapping.branchNameColumn || mapping.branchNameFromAPI) && mapping.branchValuesColumn) {
            // Get column indices
            const branchValuesIndex = sheet.columns.findIndex(col => col.name === mapping.branchValuesColumn);
            const branchNotesIndex = mapping.branchNotesColumn ?
              sheet.columns.findIndex(col => col.name === mapping.branchNotesColumn) : -1;
            const branchUomIndex = mapping.branchUomColumn ?
              sheet.columns.findIndex(col => col.name === mapping.branchUomColumn) : -1;

            // Get values from this row
            const branchName = mapping.branchNameFromAPI || mapping.branchNameColumn;
            const value = branchValuesIndex >= 0 ? parseFloat(row[branchValuesIndex]) || 0 : 0;
            const remark = branchNotesIndex >= 0 ? row[branchNotesIndex] || '' : '';
            const uom = mapping.branchUomFromAPI || (branchUomIndex >= 0 ? row[branchUomIndex] || '' : '');

            // Only add if we have a valid value
            if (value !== null && value !== undefined && value !== 0) {
              const branchItem = {
                branchName: String(branchName),
                value: value,
                uom: String(uom) // Use the mapped UOM column value or API UOM
              };

              if (remark) {
                branchItem.remark = String(remark);
              }

              branchData.push(branchItem);
            }
          }
        });

        // Add this data point to the result if it has branch data
        if (branchData.length > 0) {
          result.push({
            dataPointCode: String(dataPointCode),
            branchData: branchData
          });
        }
      });
    }
  } else {
    // Process each datapoint mapping (existing logic)
    dataPointMappings.forEach(dataPointMapping => {
      if (!dataPointMapping.dataPointCode || dataPointMapping.selectedRow === '') return;

      const rowIndex = parseInt(dataPointMapping.selectedRow);
      if (rowIndex < 0 || rowIndex >= dataRows.length) return;

      const row = dataRows[rowIndex];

      // Create branch data for this row
      const branchData = [];

      // For each branch mapping, create a branch data entry
      branchMappings.forEach(mapping => {
        if ((mapping.branchNameColumn || mapping.branchNameFromAPI) && mapping.branchValuesColumn) {
          // Get column indices
          const branchValuesIndex = sheet.columns.findIndex(col => col.name === mapping.branchValuesColumn);
          const branchNotesIndex = mapping.branchNotesColumn ?
            sheet.columns.findIndex(col => col.name === mapping.branchNotesColumn) : -1;
          const branchUomIndex = mapping.branchUomColumn ?
            sheet.columns.findIndex(col => col.name === mapping.branchUomColumn) : -1;

          // Get values from this row
          const branchName = mapping.branchNameFromAPI || mapping.branchNameColumn;
          const value = branchValuesIndex >= 0 ? parseFloat(row[branchValuesIndex]) || 0 : 0;
          const remark = branchNotesIndex >= 0 ? row[branchNotesIndex] || '' : '';
          const uom = mapping.branchUomFromAPI || (branchUomIndex >= 0 ? row[branchUomIndex] || '' : '');

          // Only add if we have a valid value
          if (value !== null && value !== undefined && value !== 0) {
            const branchItem = {
              branchName: String(branchName),
              value: value,
              uom: String(uom) // Use the mapped UOM column value or API UOM
            };

            if (remark) {
              branchItem.remark = String(remark);
            }

            branchData.push(branchItem);
          }
        }
      });

      // Add this data point to the result if it has branch data
      if (branchData.length > 0) {
        result.push({
          dataPointCode: String(dataPointMapping.dataPointCode),
          branchData: branchData
        });
      }
    });
  }

  return result;
};

const ImportWizard = ({ t }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mappings, setMappings] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState(`${new Date().getFullYear()}-${Utils.getMonthFromFrequency()}-01`);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importInProgress, setImportInProgress] = useState(false);
  const [importStatusLoading, setImportStatusLoading] = useState(true);
  const [importJobId, setImportJobId] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [statusPollingInterval, setStatusPollingInterval] = useState(null);

  const { glbRegulation } = useContext(RegulationContext);
  const totalSteps = 4;

  // Check import status on component mount
  useEffect(() => {
    checkImportStatus();
  }, []);

  const checkImportStatus = useCallback(async () => {
    try {
      setImportStatusLoading(true);
      const response = await ImportService.getImportStatus();
      const hasOngoingImport = response.data.data;
      setImportInProgress(hasOngoingImport);

      // If there's an ongoing import, start polling for status
      if (hasOngoingImport && !statusPollingInterval) {
        startStatusPolling();
      }
    } catch (error) {
      // If there's an error, assume no ongoing import to allow user to proceed
      setImportInProgress(false);
    } finally {
      setImportStatusLoading(false);
    }
  }, [statusPollingInterval]);

  const startStatusPolling = useCallback(() => {
    const interval = setInterval(async () => {
      try {
        const response = await ImportService.getImportStatus();
        const hasOngoingImport = response.data.data;

        if (!hasOngoingImport) {
          setImportStatus('COMPLETED');
          clearInterval(interval);
          setStatusPollingInterval(null);
          setImportInProgress(false);
          setCurrentStep(4);
        }
      } catch (error) {
        console.error('Error polling import status:', error);
      }
    }, 5000); // Poll every 5 seconds

    setStatusPollingInterval(interval);
  }, []);

  const stopStatusPolling = useCallback(() => {
    if (statusPollingInterval) {
      clearInterval(statusPollingInterval);
      setStatusPollingInterval(null);
    }
  }, [statusPollingInterval]);

  const handleRefreshStatus = useCallback(() => {
    checkImportStatus();
  }, [checkImportStatus]);

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      stopStatusPolling();
    };
  }, [stopStatusPolling]);

  const handleFileUpload = useCallback((file, parsedSheets) => {
    setUploadedFile(file);
    setSheets(parsedSheets);
    setSelectedSheet(null);
    setColumns([]);
    setMappings({});
    setCurrentStep(2);
  }, []);

  const handleSheetSelect = useCallback((sheet) => {
    setSelectedSheet(sheet);
    setColumns(sheet.columns);
    setMappings({});
    setCurrentStep(3);
  }, []);

  const handleColumnMapping = useCallback((newMappings) => {
    setMappings(newMappings);
  }, []);

  const handlePeriodChange = useCallback((period) => {
    setSelectedPeriod(`${period.year}-${period.month}-01`);
  }, []);

  const canProceedToNext = useCallback(() => {
    if (importInProgress) return false;

    switch (currentStep) {
      case 1:
        return uploadedFile !== null;
      case 2:
        return selectedSheet !== null;
      case 3:
        return mappings.dataPointCode && mappings.branchMappings && mappings.branchMappings.length > 0;
      default:
        return false;
    }
  }, [currentStep, uploadedFile, selectedSheet, mappings, importInProgress]);

  const nextStep = () => {
    if (canProceedToNext() && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleImport = useCallback(async () => {
    if (!glbRegulation?.id) {
      return;
    }

    setIsProcessing(true);
    try {
      // Transform the mapped data to the required API format
      const payload = transformDataToApiFormat(mappings, selectedSheet, selectedPeriod);

      // Call the API
      const response = await ImportService.uploadMetricData(
        glbRegulation.id,
        selectedPeriod,
        payload
      );

      if (response.status === 200) {
        toast.success(t('ImportWizard.status.importStarted.label'), {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 2000
        });

        // Set import in progress and start polling
        setImportInProgress(true);
        setImportJobId(response.data?.jobId || null);
        startStatusPolling();

        // Move to step 4 to show status
        setCurrentStep(4);
      }
    } catch (error) {
      toast.error(t('ImportWizard.status.failure.label'), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000
      });
    } finally {
      setIsProcessing(false);
    }
  }, [mappings, selectedSheet, selectedPeriod, glbRegulation, startStatusPolling]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <FileUpload onFileUpload={handleFileUpload} uploadedFile={uploadedFile} t={t} />;
      case 2:
        return <SheetSelector sheets={sheets} selectedSheet={selectedSheet} onSheetSelect={handleSheetSelect} t={t} />;
      case 3:
        return (
          <div>
            {/* Period Selection */}
            <div className="col-6 mb-4">
              <h6>{t("ImportWizard.reportingPeriod.title.label")}</h6>
              <ReportingPeriod
                defaultFrequency={'MONTHLY'}
                defaultPeriod={selectedPeriod}
                valueChangeCallback={handlePeriodChange} />
            </div>

            {/* Column Mapping */}
            <ColumnMapper
              columns={columns}
              mappings={mappings}
              onMappingChange={handleColumnMapping}
              isProcessing={isProcessing}
              onImport={handleImport}
              hasData={selectedSheet !== null}
              selectedSheet={selectedSheet}
              t={t}
            />
          </div>
        );
      case 4:
        return (
          <div className="text-center py-5">
            {importInProgress ? (
              <div>
                <div className="mb-4">
                  <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
                <h4 className="text-primary mb-3">{t("ImportWizard.status.processing.label")}</h4>
                <p className="text-muted mb-4">{t("ImportWizard.status.processingMessage.label")}</p>
                {importJobId && (
                  <div className="bg-light p-3 rounded">
                    <small className="text-muted">
                      <strong>{t("ImportWizard.status.jobId.label")}:</strong> {importJobId}
                    </small>
                  </div>
                )}
              </div>
            ) : importStatus === 'COMPLETED' || (!importInProgress && currentStep === 4) ? (
              <div>
                <div className="mb-4">
                  <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                </div>
                <h4 className="text-success mb-3">{t("ImportWizard.status.completed.label")}</h4>
                <p className="text-muted mb-4">{t("ImportWizard.status.completedMessage.label")}</p>

                <div className="d-flex justify-content-center gap-3">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => window.location.href = '/company_admin/dataview'}
                  >
                    <i className="fas fa-chart-bar me-2"></i>
                    {t("ImportWizard.status.viewData.label")}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="lg"
                    onClick={() => {
                      setCurrentStep(1);
                      setUploadedFile(null);
                      setSheets([]);
                      setSelectedSheet(null);
                      setColumns([]);
                      setMappings({});
                      setImportStatus(null);
                      setImportJobId(null);
                    }}
                  >
                    <i className="fas fa-plus me-2"></i>
                    {t("ImportWizard.status.newImport.label")}
                  </Button>
                </div>
              </div>
            ) : importStatus === 'FAILED' ? (
              <div>
                <div className="mb-4">
                  <i className="fas fa-times-circle text-danger" style={{ fontSize: '4rem' }}></i>
                </div>
                <h4 className="text-danger mb-3">{t("ImportWizard.status.failed.label")}</h4>
                <p className="text-muted mb-4">{t("ImportWizard.status.failedMessage.label")}</p>

                <div className="d-flex justify-content-center gap-3">
                  <Button
                    variant="outline-secondary"
                    size="lg"
                    onClick={() => {
                      setCurrentStep(1);
                      setUploadedFile(null);
                      setSheets([]);
                      setSelectedSheet(null);
                      setColumns([]);
                      setMappings({});
                      setImportStatus(null);
                      setImportJobId(null);
                    }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    {t("ImportWizard.status.tryAgain.label")}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="py-4" data-testid="import-wizard">
      <Container fluid>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="text-center">
              <h2 className="mb-2">{t("ImportWizard.title.label")}</h2>
              <p className="text-muted">{t("ImportWizard.subtitle.label")}</p>
            </div>
          </Col>
        </Row>

        {/* Import Status Warning */}
        {importStatusLoading ? (
          <Row className="mb-4">
            <Col>
              <Alert variant="info" className="d-flex align-items-center">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                {t("ImportWizard.status.checking.label")}
              </Alert>
            </Col>
          </Row>
        ) : importInProgress ? (
          <Row className="mb-4">
            <Col>
              <Alert variant="warning" className="d-flex align-items-center justify-content-between">
                <div>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>{t("ImportWizard.status.importInProgress.label")}</strong>
                  <p className="mb-0 mt-1">{t("ImportWizard.status.importInProgressMessage.label")}</p>
                </div>
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={handleRefreshStatus}
                  disabled={importStatusLoading}
                >
                  <i className="fas fa-refresh me-1"></i>
                  {t("ImportWizard.status.refreshStatus.label")}
                </Button>
              </Alert>
            </Col>
          </Row>
        ) : null}

        {/* Progress Bar */}
        <Row className="mb-4">
          <Col>
            <ProgressBar
              now={(currentStep / totalSteps) * 100}
              variant="success"
            />
            <div className="text-center mt-2 fw-500">
              {t("ImportWizard.stepIndicator.label", { current: currentStep, total: totalSteps })}
            </div>
          </Col>
        </Row>

        {/* Wizard Content */}
        <Card>
          <Card.Body>
            {renderStepContent()}
          </Card.Body>
        </Card>

        {/* Navigation Buttons */}
        {currentStep < 4 && (
          <Row className="mt-4">
            <Col className="d-flex justify-content-between">
              <Button
                variant="outline-secondary"
                onClick={prevStep}
                disabled={currentStep === 1}
              >
                {t("ImportWizard.navigation.back.label")}
              </Button>

              <div>
                {currentStep < 3 ? (
                  <Button
                    variant="primary"
                    onClick={nextStep}
                    disabled={!canProceedToNext() || importInProgress}
                  >
                    {t("ImportWizard.navigation.next.label")}
                  </Button>
                ) : (
                  <Button
                    variant="success"
                    onClick={handleImport}
                    disabled={isProcessing || importInProgress}
                  >
                    {isProcessing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {t("ImportWizard.navigation.importing.label")}
                      </>
                    ) : (
                      t("ImportWizard.navigation.import.label")
                    )}
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default withTranslation()(ImportWizard); 